import React from 'react';

import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import DownIcon from '@atlaskit/icon/glyph/chevron-down';
import {
  PopupSelect,
  components,
  MenuProps,
  ActionMeta,
  OptionType,
  FormatOptionLabelMeta,
} from '@atlaskit/select';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';

import { useIntl } from 'src/hooks/intl';
import {
  getFilterState,
  getRelevantSites,
} from 'src/redux/jira/selectors/repo-page-selectors';
import { Project, Site } from 'src/redux/jira/types';

import { chooserStyle } from './chooser-style';
import {
  DownIconWrapper,
  TargetButton,
  TargetButtonText,
} from './popup-select-target.styled';
import messages from './project-chooser-jira-tab.i18n';
import {
  CustomOption,
  ProjectIcon,
  ProjectLabel,
  OptionSiteLabel,
  LabelContainer,
  OptionSiteLabelSelected,
  ProjectDropdownTextContainer,
  MenuContainer,
} from './project-chooser-jira-tab.styled';

type Props = {
  isLoading: boolean;
  isDisabled: boolean;
  onSelectedChange: (selected: Project) => void;
  projects: Project[];
  selected?: Project;
};

type ProjectOptionType = {
  value: string;
  label: string;
  project: Project;
};

// @ts-ignore TODO: fix noImplicitAny error here
const formatOptionLabel = (
  option: ProjectOptionType,
  labelMeta: FormatOptionLabelMeta<ProjectOptionType>
) => {
  if (labelMeta.context === 'menu' && labelMeta.selectValue) {
    const siteName = option.project.site.cloudName;
    return (
      <CustomOption>
        <ProjectIcon src={option.project.avatarUrls['16x16']} />
        <LabelContainer>
          <div>{option.label}</div>
          {'value' in labelMeta.selectValue &&
          option.value === labelMeta.selectValue.value ? (
            <OptionSiteLabelSelected>{siteName}</OptionSiteLabelSelected>
          ) : (
            <OptionSiteLabel>{siteName}</OptionSiteLabel>
          )}
        </LabelContainer>
      </CustomOption>
    );
  }
  return option.label;
};

export const createInJiraLink = (site: Site | undefined) => {
  if (!site) {
    return null;
  }

  return (
    <a target="_blank" href={`${site.cloudUrl}/secure/CreateIssue.jspa`}>
      <FormattedMessage {...messages.goToJira} />
    </a>
  );
};

const targetLabel = (isLoading: boolean, selected: Project | undefined) => {
  if (!isLoading && selected) {
    return `${selected.name} (${selected.key})`;
  }
  return '';
};

export const ProjectChooserJiraTab = ({
  isLoading,
  isDisabled,
  onSelectedChange,
  projects,
  selected,
}: Props) => {
  const intl = useIntl();
  const { siteCloudId } = useSelector(getFilterState);
  const relevantSites: Site[] = useSelector(getRelevantSites);
  const currentSite = relevantSites.find(site => site.cloudId === siteCloudId);

  const Menu = (props: MenuProps<OptionType>) => {
    return (
      <MenuContainer>
        <components.Menu {...props}>
          <>
            {props.children}
            <ProjectDropdownTextContainer>
              <FormattedMessage
                {...messages.createIssuesInOtherProjects}
                values={{
                  createInJiraLink: createInJiraLink(currentSite),
                }}
              />
            </ProjectDropdownTextContainer>
          </>
        </components.Menu>
      </MenuContainer>
    );
  };

  if (!isLoading && projects.length === 1) {
    return (
      <ProjectLabel>{`${projects[0].name} (${projects[0].key})`}</ProjectLabel>
    );
  }
  const handleSelectProject = (
    option: ProjectOptionType,
    action: ActionMeta
  ) => {
    if (action.action === 'select-option') {
      onSelectedChange(option.project);
    }
  };

  const groupHeadingStyle = (base: Record<string, any>) => ({
    ...base,
    color: token('color.text.subtlest'),
    font: token('font.body'),
  });

  return (
    <PopupSelect
      // We do our own filtering in handleInputChange, no need to filter again
      formatOptionLabel={formatOptionLabel}
      styles={{ ...chooserStyle, groupHeading: groupHeadingStyle }}
      maxMenuHeight={400}
      spacing="compact"
      isLoading={isLoading}
      isDisabled={isDisabled}
      onClickPreventDefault={false}
      backspaceRemovesValue={false}
      searchThreshold={99}
      value={
        selected
          ? {
              label: selected.name,
              value: selected.id + selected.site.cloudName,
              project: selected,
            }
          : undefined
      }
      onChange={handleSelectProject}
      options={[
        {
          label: intl.formatMessage(messages.projectsHeader),
          options: projects.map(project => ({
            label: `${project.name} (${project.key})`,
            value: project.id + project.site.cloudName,
            project,
          })),
        },
      ]}
      target={({ ref }) => (
        <TargetButton
          testId="project-chooser-jira-tab-target-button"
          appearance="subtle"
          ref={ref}
          iconAfter={
            isLoading ? (
              <Spinner size="small" />
            ) : (
              <DownIconWrapper>
                <DownIcon label="expand site chooser" />
              </DownIconWrapper>
            )
          }
          isDisabled={isDisabled}
          spacing="compact"
        >
          <TargetButtonText>
            {targetLabel(isLoading, selected)}
          </TargetButtonText>
        </TargetButton>
      )}
      components={{ Menu }}
    />
  );
};
